/**
 * Staff Credentials Page - Admin
 * Manage staff certifications, licenses, and credentials
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../api';
import {
  Award, Search, Plus, Edit2, Trash2, RefreshCw, Calendar,
  AlertTriangle, CheckCircle, Clock, FileText, Download, Filter,
  User, Shield, Mail, X, ChevronDown, Upload
} from 'lucide-react';

const StaffCredentialsPage = () => {
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);
  const [staff, setStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    staffId: '',
    credentialType: 'license',
    name: '',
    issuingAuthority: '',
    licenseNumber: '',
    issueDate: '',
    expirationDate: '',
    notes: ''
  });

  const credentialTypes = [
    { value: 'license', label: 'Professional License' },
    { value: 'certification', label: 'Certification' },
    { value: 'training', label: 'Training Certificate' },
    { value: 'cpr', label: 'CPR/BLS Certification' },
    { value: 'background', label: 'Background Check' },
    { value: 'physical', label: 'Physical/TB Test' },
    { value: 'immunization', label: 'Immunization Record' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [credRes, staffRes] = await Promise.all([
        api.get('/admin/credentials'),
        api.get('/auth/admin/users')
      ]);
      
      setCredentials(credRes.data?.credentials || []);
      setStaff(staffRes.data?.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const getMockCredentials = () => [
    { 
      id: '1', 
      staffId: '1', 
      staffName: 'Sarah Johnson', 
      credentialType: 'license',
      name: 'RN License',
      issuingAuthority: 'California Board of Nursing',
      licenseNumber: 'RN-123456',
      issueDate: '2022-01-15',
      expirationDate: '2025-01-15',
      status: 'active'
    },
    { 
      id: '2', 
      staffId: '1', 
      staffName: 'Sarah Johnson', 
      credentialType: 'cpr',
      name: 'BLS Certification',
      issuingAuthority: 'American Heart Association',
      licenseNumber: 'BLS-789012',
      issueDate: '2024-03-01',
      expirationDate: '2026-03-01',
      status: 'active'
    },
    { 
      id: '3', 
      staffId: '2', 
      staffName: 'Michael Chen', 
      credentialType: 'license',
      name: 'LVN License',
      issuingAuthority: 'California Board of Nursing',
      licenseNumber: 'LVN-456789',
      issueDate: '2021-06-20',
      expirationDate: '2025-02-20',
      status: 'expiring'
    },
    { 
      id: '4', 
      staffId: '3', 
      staffName: 'Emily Rodriguez', 
      credentialType: 'certification',
      name: 'OASIS-E Certification',
      issuingAuthority: 'CMS',
      licenseNumber: 'OASIS-2024-001',
      issueDate: '2024-01-10',
      expirationDate: '2025-01-10',
      status: 'expiring'
    },
    { 
      id: '5', 
      staffId: '4', 
      staffName: 'David Wilson', 
      credentialType: 'background',
      name: 'Background Check',
      issuingAuthority: 'State of California',
      licenseNumber: 'BC-2023-5678',
      issueDate: '2023-08-15',
      expirationDate: '2024-08-15',
      status: 'expired'
    }
  ];

  const getStatus = (expirationDate) => {
    const today = new Date();
    const expDate = new Date(expirationDate);
    const daysUntilExpiry = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'active';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Active</span>;
      case 'expiring':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" />Expiring Soon</span>;
      case 'expired':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />Expired</span>;
      default:
        return null;
    }
  };

  const filteredCredentials = credentials.filter(cred => {
    const matchesSearch = 
      cred.staffName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cred.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cred.licenseNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || cred.status === filterStatus;
    const matchesType = filterType === 'all' || cred.credentialType === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: credentials.length,
    active: credentials.filter(c => c.status === 'active').length,
    expiring: credentials.filter(c => c.status === 'expiring').length,
    expired: credentials.filter(c => c.status === 'expired').length
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingCredential) {
        await api.put(`/admin/credentials/${editingCredential.id}`, formData);
        toast.success('Credential updated successfully');
      } else {
        await api.post('/admin/credentials', formData);
        toast.success('Credential added successfully');
      }
      setShowAddModal(false);
      setEditingCredential(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credential');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    try {
      await api.delete(`/admin/credentials/${id}`);
      toast.success('Credential deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete credential');
    }
  };

  const handleEdit = (cred) => {
    setEditingCredential(cred);
    setFormData({
      staffId: cred.staffId,
      credentialType: cred.credentialType,
      name: cred.name,
      issuingAuthority: cred.issuingAuthority,
      licenseNumber: cred.licenseNumber,
      issueDate: cred.issueDate,
      expirationDate: cred.expirationDate,
      notes: cred.notes || ''
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      staffId: '',
      credentialType: 'license',
      name: '',
      issuingAuthority: '',
      licenseNumber: '',
      issueDate: '',
      expirationDate: '',
      notes: ''
    });
  };

  const handleSendReminder = async (cred) => {
    try {
      await api.post(`/admin/credentials/${cred.id}/reminder`);
      toast.success(`Reminder sent to ${cred.staffName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-credentials-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Award className="w-6 h-6 text-orange-500" />
              Staff Credentials
            </h1>
            <p className="text-gray-600 mt-1">Manage licenses, certifications, and compliance documents</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" data-testid="send-expiry-alerts-btn" onClick={async () => {
              try { const r = await api.post('/admin/credentials/send-expiry-alerts'); toast.success(r.data.sent ? `${r.data.sent} expiry email(s) sent` : 'No new expiry alerts due - each alert (30 days, 7 days, expired) is sent once'); }
              catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not send alerts'); }
            }}>
              Send expiry alerts now
            </Button>
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button 
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="bg-orange-500 hover:bg-orange-600 gap-2"
              data-testid="add-credential-btn"
            >
              <Plus className="w-4 h-4" />
              Add Credential
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Credentials</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expiring Soon</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.expiring}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by staff name, credential, or license number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  data-testid="credential-search"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Types</option>
              {credentialTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <Button variant="ghost" onClick={fetchData} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Credentials Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full" data-testid="credentials-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Staff Member</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Credential</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">License #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Expiration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-orange-500" />
                    <p className="mt-2">Loading credentials...</p>
                  </td>
                </tr>
              ) : filteredCredentials.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No credentials found
                  </td>
                </tr>
              ) : (
                filteredCredentials.map((cred) => (
                  <tr key={cred.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-orange-600" />
                        </div>
                        <span className="font-medium text-gray-900">{cred.staffName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{cred.name}</p>
                        <p className="text-xs text-gray-500">{cred.issuingAuthority}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                        {credentialTypes.find(t => t.value === cred.credentialType)?.label || cred.credentialType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-sm">{cred.licenseNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{new Date(cred.expirationDate).toLocaleDateString()}</td>
                    <td className="px-4 py-3">{getStatusBadge(cred.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {(cred.status === 'expiring' || cred.status === 'expired') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendReminder(cred)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Send Reminder"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(cred)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cred.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Expiring Soon Alert */}
        {stats.expiring > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Credentials Expiring Soon</p>
                <p className="text-sm text-yellow-700 mt-1">
                  {stats.expiring} credential(s) will expire within the next 30 days. 
                  Please ensure renewals are processed before expiration to maintain compliance.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b bg-orange-500 text-white rounded-t-xl flex items-center justify-between sticky top-0">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Award className="w-5 h-5" />
                {editingCredential ? 'Edit Credential' : 'Add New Credential'}
              </h2>
              <button 
                onClick={() => { setShowAddModal(false); setEditingCredential(null); }}
                className="hover:bg-white/20 rounded p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member *</label>
                <select
                  required
                  value={formData.staffId}
                  onChange={(e) => setFormData({...formData, staffId: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                >
                  <option value="">Select Staff Member</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credential Type *</label>
                <select
                  required
                  value={formData.credentialType}
                  onChange={(e) => setFormData({...formData, credentialType: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                >
                  {credentialTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credential Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  placeholder="e.g., RN License, BLS Certification"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Authority *</label>
                <input
                  type="text"
                  required
                  value={formData.issuingAuthority}
                  onChange={(e) => setFormData({...formData, issuingAuthority: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  placeholder="e.g., California Board of Nursing"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License/Certificate Number *</label>
                <input
                  type="text"
                  required
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  placeholder="e.g., RN-123456"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.issueDate}
                    onChange={(e) => setFormData({...formData, issueDate: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.expirationDate}
                    onChange={(e) => setFormData({...formData, expirationDate: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowAddModal(false); setEditingCredential(null); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {editingCredential ? 'Update Credential' : 'Add Credential'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default StaffCredentialsPage;
